import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";
import { z } from "zod";
import { brandsQuery, usageQuery } from "../../../api/admin";
import type { UsageRow } from "../../../api/types";
import { requireAdmin } from "../../../auth/useMe";
import { PeriodMeters, Statements } from "../../../components/billing/PeriodUsage";
import { DataTable } from "../../../components/DataTable";
import { TextingUsageSection } from "../../../components/messaging/TextingUsageSection";
import {
  Card,
  cx,
  EmptyState,
  ErrorState,
  Input,
  Loading,
  Segmented,
  Select,
  Stat,
} from "../../../components/ui";
import { addDays, fmtDate, fmtNumber, fmtUsd, todayIso } from "../../../lib/format";
import { useNavigate } from "../../../lib/navigate";

const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const searchSchema = z.object({
  from: day.optional().catch(undefined),
  to: day.optional().catch(undefined),
  by: z.enum(["user", "day"]).optional().catch(undefined),
  brand: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/admin/billing/usage")({
  staticData: { crumb: { label: "Usage", parents: [] } },
  beforeLoad: requireAdmin,
  validateSearch: (s) => searchSchema.parse(s),
  component: UsagePage,
});

interface Agg {
  key: string;
  label: string;
  call_minutes: number;
  transcription_hours: number;
  sms_count: number;
  est_cost_usd: number;
}

export function aggregateUsage(rows: UsageRow[], by: "user" | "day"): Agg[] {
  const map = new Map<string, Agg>();
  for (const r of rows) {
    const key = by === "user" ? r.user_id : r.date;
    const a = map.get(key) ?? {
      key,
      label: by === "user" ? r.user_name : r.date,
      call_minutes: 0,
      transcription_hours: 0,
      sms_count: 0,
      est_cost_usd: 0,
    };
    a.call_minutes += r.call_minutes;
    a.transcription_hours += r.transcription_hours;
    a.sms_count += r.sms_count;
    a.est_cost_usd += r.est_cost_usd;
    map.set(key, a);
  }
  return [...map.values()];
}

const col = createColumnHelper<Agg>();

function UsagePage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const to = search.to ?? todayIso();
  const from = search.from ?? addDays(to, -29);
  const by = search.by ?? "user";
  const usage = useQuery(usageQuery({ from, to }));
  const brands = useQuery(brandsQuery);
  const rows = useMemo(
    () => (usage.data?.rows ?? []).filter((r) => !search.brand || r.brand_id === search.brand),
    [usage.data, search.brand],
  );
  const agg = useMemo(() => aggregateUsage(rows, by), [rows, by]);
  // Without the Telnyx grant the server withholds the estimate (ADR 0050): no column, no tile.
  const costShown = usage.data?.est_cost_shown !== false;
  const total = useMemo(
    () =>
      aggregateUsage(
        rows.map((r) => ({ ...r, user_id: "all", date: "all" })),
        "user",
      )[0],
    [rows],
  );

  const columns = useMemo(
    () => [
      col.accessor("label", {
        header: by === "user" ? "User" : "Day",
        cell: (c) =>
          by === "day" ? (
            <span className="tnum">{fmtDate(c.getValue())}</span>
          ) : (
            <span className="font-medium">{c.getValue()}</span>
          ),
      }),
      col.accessor("call_minutes", {
        header: "Call minutes",
        meta: { align: "right" },
        cell: (c) => fmtNumber(c.getValue()),
      }),
      col.accessor("transcription_hours", {
        header: "Transcription hours",
        meta: { align: "right" },
        cell: (c) => fmtNumber(c.getValue(), 1),
      }),
      col.accessor("sms_count", {
        header: "Texts",
        meta: { align: "right" },
        cell: (c) => fmtNumber(c.getValue()),
      }),
      ...(costShown
        ? [
            col.accessor("est_cost_usd", {
              header: "Estimated cost",
              meta: { align: "right" },
              cell: (c) => fmtUsd(c.getValue()),
            }),
          ]
        : []),
    ],
    [by, costShown],
  );

  const set = (patch: Partial<z.infer<typeof searchSchema>>) =>
    navigate({ search: (p) => ({ ...p, ...patch }), replace: true });

  return (
    <>
      <PeriodMeters />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Input
          type="date"
          aria-label="From date"
          className="w-40"
          value={from}
          max={to}
          onChange={(e) => e.target.value && set({ from: e.target.value })}
        />
        <span className="text-ui text-ink-2">to</span>
        <Input
          type="date"
          aria-label="To date"
          className="w-40"
          value={to}
          min={from}
          onChange={(e) => e.target.value && set({ to: e.target.value })}
        />
        <Select
          aria-label="Brand"
          className="w-40"
          value={search.brand ?? ""}
          onValueChange={(v) => set({ brand: v || undefined })}
          options={[
            { value: "", label: "All brands" },
            ...(brands.data ?? []).map((b) => ({ value: b.id, label: b.name })),
          ]}
        />
        <span className="ml-auto">
          <Segmented
            label="Group by"
            value={by}
            onChange={(v) => set({ by: v === "user" ? undefined : v })}
            options={[
              { value: "user", label: "By user" },
              { value: "day", label: "By day" },
            ]}
          />
        </span>
      </div>
      {usage.isPending ? (
        <Loading />
      ) : usage.isError ? (
        <ErrorState error={usage.error} onRetry={() => usage.refetch()} />
      ) : (
        <>
          <div className={cx("mb-4 grid grid-cols-2 gap-3", costShown ? "lg:grid-cols-4" : "lg:grid-cols-3")}>
            <Stat label="Call minutes" value={fmtNumber(total?.call_minutes ?? 0)} />
            <Stat label="Transcription hours" value={fmtNumber(total?.transcription_hours ?? 0, 1)} />
            <Stat label="Texts" value={fmtNumber(total?.sms_count ?? 0)} />
            {costShown ? (
              <Stat
                label="Estimated cost"
                value={fmtUsd(total?.est_cost_usd ?? 0)}
                sub="From the rate table, before taxes and fees"
              />
            ) : null}
          </div>
          <Card>
            <DataTable
              data={agg}
              columns={columns}
              getRowId={(a) => a.key}
              initialSorting={
                by === "user"
                  ? [{ id: costShown ? "est_cost_usd" : "call_minutes", desc: true }]
                  : [{ id: "label", desc: true }]
              }
              empty={<EmptyState title="No usage in this range" />}
            />
          </Card>
          <TextingUsageSection from={from} to={to} costShown={costShown} />
        </>
      )}
      <Statements />
    </>
  );
}
